import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../shared';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectProps {
  label: string;
  options: Option[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  allLabel?: string;
  /**
   * Restyles the trigger only (merged via cn). The People Tickets pages use it for their single
   * radius and type scale; other callers keep the default look.
   */
  triggerClassName?: string;
  /**
   * Which edge of the trigger the panel lines up with. `right` (the default) suits a filter at the
   * right of a bar; one at the left edge of the page opens `left`, or it would run off the page.
   */
  align?: 'left' | 'right';
}

/** Space kept between the panel and the window's edge, and the least height worth opening into. */
const PANEL_MARGIN = 12;
const MIN_PANEL_HEIGHT = 200;

/**
 * A checkbox-dropdown multi-select. Not a design-system atom — the DS Select is single-value —
 * so this composes plain Tailwind + native checkboxes, matching how other non-covered composite
 * patterns in this codebase (e.g. CardBadges) fall back to plain elements.
 */
export function MultiSelect({ label, options, selected, onChange, allLabel = 'All', triggerClassName, align = 'right' }: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Where the panel fits: below the trigger unless there is clearly more room above, and never
  // taller than that room — a short page left the last options past the bottom of the scroll area.
  const [fit, setFit] = useState<{ up: boolean; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - PANEL_MARGIN;
    const above = rect.top - PANEL_MARGIN;
    const up = below < MIN_PANEL_HEIGHT && above > below;
    setFit({ up, maxHeight: Math.max(MIN_PANEL_HEIGHT, up ? above : below) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  function toggle(value: string) {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  }

  const allSelected = options.every((opt) => selected.has(opt.value));
  const noneSelected = selected.size === 0;
  const summary = noneSelected || allSelected ? allLabel : `${selected.size} selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 before:hidden after:hidden',
          triggerClassName,
        )}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="text-gray-500">{label}:</span>
        <span>{summary}</span>
      </button>
      {open && (
        <div
          className={cn(
            'absolute z-20 w-max min-w-[220px] max-w-[min(90vw,28rem)] overflow-y-auto rounded-md border border-gray-200 bg-white p-1.5 shadow-lg',
            align === 'left' ? 'left-0' : 'right-0',
            fit?.up ? 'bottom-full mb-1' : 'mt-1',
          )}
          style={fit ? { maxHeight: fit.maxHeight } : undefined}
        >
          <div className="mb-1 flex items-center gap-1.5 border-b border-gray-100 pb-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 body-12-semibold"
              disabled={allSelected}
              onClick={() => onChange(new Set(options.map((o) => o.value)))}
            >
              Select all
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 body-12-semibold"
              disabled={noneSelected}
              onClick={() => onChange(new Set())}
            >
              Select none
            </Button>
          </div>
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50"
            >
              <input type="checkbox" checked={selected.has(opt.value)} onChange={() => toggle(opt.value)} />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
